import { useEffect, useState } from "react";
import { api, apiError, API_BASE } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Smartphone, Apple } from "lucide-react";
import { Stat, Section, Loading, Empty, useFetch, dt } from "./_shared";

const RANGES = [
  { d: 7, label: "7 Tage" },
  { d: 30, label: "30 Tage" },
  { d: 90, label: "90 Tage" },
];

function num(n) {
  return (Number(n) || 0).toLocaleString("de-DE");
}

function BarList({ rows, testId, emptyText }) {
  if (!rows?.length) return <Empty text={emptyText || "Keine Daten."} />;
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="space-y-2" data-testid={testId}>
      {rows.map((r) => (
        <div key={r.key}>
          <div className="mb-0.5 flex items-center justify-between gap-2 text-sm">
            <span className="truncate text-foreground">{r.key}</span>
            <span className="shrink-0 font-semibold text-foreground">{num(r.count)}</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
            <div className="h-full rounded-full bg-sky-500" style={{ width: `${Math.round((r.count / max) * 100)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function DayBars({ rows }) {
  if (!rows?.length) return <Empty text="Noch keine Klicks im Zeitraum." />;
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="flex items-end gap-0.5 overflow-x-auto pb-1" data-testid="sr-by-day">
      {rows.map((r) => (
        <div key={r.key} className="flex min-w-[6px] flex-1 flex-col items-center" title={`${r.key}: ${num(r.count)}`}>
          <div className="flex w-full flex-col justify-end" style={{ height: 110 }}>
            <div className="w-full rounded-sm bg-violet-500" style={{ height: `${Math.round((r.count / max) * 100)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function SmartRedirect() {
  const { data: cfg, loading, reload } = useFetch("/admin/smart-redirect/config");
  const [days, setDays] = useState(30);
  const { data: stats } = useFetch(`/admin/smart-redirect/analytics?days=${days}`);

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (cfg) {
      setForm({
        enabled: !!cfg.enabled,
        pathsText: (cfg.paths || []).join("\n"),
        ios_url: cfg.ios_url || "",
        android_url: cfg.android_url || "",
        bot_bypass: cfg.bot_bypass !== false,
      });
    }
  }, [cfg]);

  const save = async () => {
    setSaving(true);
    try {
      const paths = form.pathsText.split("\n").map((p) => p.trim()).filter(Boolean);
      await api.post("/admin/smart-redirect/config", {
        enabled: form.enabled,
        paths,
        ios_url: form.ios_url.trim(),
        android_url: form.android_url.trim(),
        bot_bypass: form.bot_bypass,
      });
      toast.success("Konfiguration gespeichert.");
      await reload();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    setForm({
      enabled: true,
      pathsText: "/\n/for-you",
      ios_url: "",
      android_url: "https://play.google.com/store/apps/details?id=com.mywithin.app",
      bot_bypass: true,
    });
    toast.info('Standardwerte eingesetzt – zum Übernehmen „Speichern" klicken.');
  };

  const testHop = (platform) => {
    window.open(`${API_BASE}/go/${platform}?src=admin_test&path=/`, "_blank", "noopener");
  };

  if (loading || !form) return <Loading />;

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Admin · Technik & Betrieb" title="Smart Redirect" subtitle="Mobile-Besucher (Instagram & Co.) automatisch in den passenden App Store leiten." />

      {/* 1 · Config */}
      <Section title="Konfiguration" testId="sr-config"
        action={<span className="text-xs text-muted-foreground">{cfg?.updated_at ? `Zuletzt: ${dt(cfg.updated_at)}${cfg.updated_by ? ` · ${cfg.updated_by}` : ""}` : ""}</span>}>
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background p-3">
            <div>
              <p className="text-sm font-medium text-foreground">Aktiv (Master-Kill-Switch)</p>
              <p className="text-xs text-muted-foreground">Schaltet alle Weiterleitungen global an/aus.</p>
            </div>
            <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="sr-enabled" />
          </div>

          <div>
            <Label htmlFor="sr-paths" className="text-sm">Pfade (eine Zeile = ein Pfad; Wildcard <code>*</code> erlaubt)</Label>
            <Textarea id="sr-paths" rows={4} className="mt-1 font-mono text-sm" value={form.pathsText}
              onChange={(e) => setForm({ ...form, pathsText: e.target.value })} placeholder={"/\n/for-you\n/blog/*"} data-testid="sr-paths" />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="sr-android" className="text-sm">Android Play Store URL</Label>
              <Input id="sr-android" className="mt-1" value={form.android_url}
                onChange={(e) => setForm({ ...form, android_url: e.target.value })}
                placeholder="https://play.google.com/store/apps/details?id=..." data-testid="sr-android-url" />
            </div>
            <div>
              <Label htmlFor="sr-ios" className="text-sm">iOS App Store URL (leer = bleibt auf Homepage)</Label>
              <Input id="sr-ios" className="mt-1" value={form.ios_url}
                onChange={(e) => setForm({ ...form, ios_url: e.target.value })}
                placeholder="https://apps.apple.com/app/..." data-testid="sr-ios-url" />
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background p-3">
            <div>
              <p className="text-sm font-medium text-foreground">Bot-Bypass (immer AN lassen — SEO/LLM)</p>
              <p className="text-xs text-muted-foreground">Googlebot, GPTBot, Claude, Perplexity &amp; Co. werden nie umgeleitet.</p>
            </div>
            <Switch checked={form.bot_bypass} onCheckedChange={(v) => setForm({ ...form, bot_bypass: v })} data-testid="sr-bot-bypass" />
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={save} disabled={saving} className="rounded-full" data-testid="sr-save">{saving ? "Speichern…" : "Speichern"}</Button>
            <Button onClick={resetDefaults} variant="outline" className="rounded-full" data-testid="sr-reset">Auf Defaults zurücksetzen</Button>
          </div>
        </div>
      </Section>

      {/* 3 · Live-Test */}
      <Section title="Live-Test" testId="sr-livetest">
        <p className="mb-3 text-sm text-muted-foreground">Öffnet den echten Redirect-Hop in einem neuen Tab (wird als <code>admin_test</code>-Klick geloggt).</p>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => testHop("android")} variant="outline" className="rounded-full" data-testid="sr-test-android"><Smartphone className="mr-1.5 h-4 w-4" />Android-Hop testen</Button>
          <Button onClick={() => testHop("ios")} variant="outline" className="rounded-full" data-testid="sr-test-ios"><Apple className="mr-1.5 h-4 w-4" />iOS-Hop testen</Button>
        </div>
      </Section>

      {/* 2 · Analytics */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">Analytics</h2>
        <div className="flex gap-2" data-testid="sr-ranges">
          {RANGES.map((r) => (
            <button key={r.d} onClick={() => setDays(r.d)} data-testid={`sr-range-${r.d}`}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${days === r.d ? "bg-foreground text-background" : "bg-secondary text-muted-foreground hover:bg-secondary/70"}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="sr-kpis">
        <Stat label="Klicks gesamt" value={num(stats?.total)} testId="sr-total" />
        <Stat label="Instagram" value={num(stats?.instagram)} testId="sr-instagram" />
        <Stat label="Android" value={num(stats?.by_platform?.find((p) => p.key === "android")?.count)} testId="sr-android-count" />
        <Stat label="iOS" value={num(stats?.by_platform?.find((p) => p.key === "ios")?.count)} testId="sr-ios-count" />
      </div>

      <Section title="Zeitreihe (Klicks / Tag)" testId="sr-timeseries">
        <DayBars rows={stats?.by_day} />
      </Section>

      <div className="grid gap-5 lg:grid-cols-3">
        <Section title="Nach Plattform" testId="sr-platform"><BarList rows={stats?.by_platform} emptyText="Keine Daten." /></Section>
        <Section title="Nach Quelle (UTM)" testId="sr-source"><BarList rows={stats?.by_source} emptyText="Keine Daten." /></Section>
        <Section title="Nach Pfad" testId="sr-path"><BarList rows={stats?.by_path} emptyText="Keine Daten." /></Section>
      </div>
    </div>
  );
}
